import React, { Fragment } from 'react';
import { SortOrder } from '@kirin/api-client/contracts/helpers';
import { DashboardWidgetWrapper } from '@/pages/organization/components/DashboardWidgetWrapper';
import { MCPUsageUserList } from '@/pages/organization/components/WidgetUserLists/MCPUsageUserList.tsx';
import { useParams } from 'react-router-dom';
import { useMCPServers } from '@/hooks/api/useMCPServers';
import type { MCPServerUsage } from './types';
import { UsageRow } from '@/pages/organization/components/UsageRow';

const buildMCPUsageStats = (
  servers: { name: string; userCount: number }[]
): MCPServerUsage[] => {
  if (!servers.length) return [];

  // Use the highest server count as the total (for percentage calculations)
  const maxCount = Math.max(...servers.map(s => s.userCount));

  return servers.map(server => ({
    id: server.name,
    name: server.name,
    usage: server.userCount,
    total: maxCount,
  }));
};

const LIMIT = 10;

export const MCPServersUsageWidget: React.FC = () => {
  const { organizationId } = useParams<{ organizationId: string }>();
  const { data, isLoading, isError, error, refetch } = useMCPServers(
    organizationId,
    { sortBy: 'userCount', sortOrder: SortOrder.Desc, limit: LIMIT }
  );

  const usageData: MCPServerUsage[] = buildMCPUsageStats(
    data?.data?.map((s: { name: string; userCount: number }) => ({
      name: s.name,
      userCount: s.userCount,
    })) || []
  );

  const total = usageData[0]?.total ?? 0;
  const [selectedServer, setSelectedServer] =
    React.useState<MCPServerUsage | null>(null);

  const handleRowClick = (server: MCPServerUsage) => {
    setSelectedServer(server);
  };

  const handleCloseModal = () => {
    setSelectedServer(null);
  };

  return (
    <Fragment>
      <DashboardWidgetWrapper
        className="xl:col-span-8 min-h-0"
        title="Top MCP Servers by Usage"
        description="Shows up to 10 MCP Servers"
        isLoading={isLoading}
        isError={isError || !!error}
        refetch={refetch}
      >
        <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden space-y-4 pr-2">
          {usageData.map(server => (
            <UsageRow
              key={server.id}
              id={server.id}
              name={server.name}
              usage={server.usage}
              total={total}
              onClick={() => handleRowClick(server)}
            />
          ))}
        </div>
      </DashboardWidgetWrapper>
      <MCPUsageUserList
        organizationId={organizationId || ''}
        serverName={selectedServer ? selectedServer.name : ''}
        open={selectedServer !== null}
        onClose={handleCloseModal}
      />
    </Fragment>
  );
};
